import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { IconX } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

// ── "Open from where you clicked" ───────────────────────────────────────────
// Track the most recent pointer-down globally, then pivot each dialog's entrance
// zoom at that point so the modal appears to grow from where the user clicked
// (instead of always from the centre). Read ONCE when the content mounts and
// applied as an inline style, so the CSS enter animation uses it from frame 1.
// Falls back to the default centred zoom for keyboard / programmatic opens (no
// recent click). Percentage origin → no measurement needed; works for centred
// AND fullscreen dialogs alike.
let __lastPointer = null;
if (typeof window !== 'undefined') {
  window.addEventListener(
    'pointerdown',
    (e) => { __lastPointer = { x: e.clientX, y: e.clientY, t: Date.now() }; },
    { capture: true, passive: true }
  );
}
const SMOOTH_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'; // ease-out-expo — soft, no overshoot
const SMOOTH_DUR = '300ms';
function useDialogEnterStyle(className) {
  return React.useMemo(() => {
    const cls = className || '';
    // Fullscreen / edge-anchored panels cancel the base -translate-1/2 centering with
    // translate-x-0 / translate-y-0 / inset-0. A click-pivot OR the default corner-slide
    // makes those fly in from the side (the "opens from the left" glitch), so give them a
    // smooth in-place fade + a barely-there zoom instead, centred on themselves.
    const isPanel = cls.includes('translate-x-0') || cls.includes('translate-y-0') || cls.includes('inset-0');
    if (isPanel) {
      return {
        transformOrigin: 'center',
        '--tw-enter-scale': '0.985',
        '--tw-enter-translate-x': '0px',
        '--tw-enter-translate-y': '0px',
        animationDuration: SMOOTH_DUR,
        animationTimingFunction: SMOOTH_EASE,
      };
    }
    // Centred dialogs: soft zoom that GROWS from the last click point. enter-translate
    // stays -50% (matching the base centering) so the modal stays centred and only scales
    // — the keyframe transform REPLACES the base, so this must carry the -50% or it flies
    // in from a corner. Falls back to a centred zoom for keyboard / programmatic opens.
    const base = {
      '--tw-enter-scale': '0.9',
      '--tw-enter-translate-x': '-50%',
      '--tw-enter-translate-y': '-50%',
      animationDuration: SMOOTH_DUR,
      animationTimingFunction: SMOOTH_EASE,
    };
    const p = __lastPointer;
    if (!p || typeof window === 'undefined' || Date.now() - p.t > 1200) return base;
    const vw = window.innerWidth || 1;
    const vh = window.innerHeight || 1;
    const ox = Math.max(0, Math.min(100, (p.x / vw) * 100)).toFixed(2);
    const oy = Math.max(0, Math.min(100, (p.y / vh) * 100)).toFixed(2);
    return { ...base, transformOrigin: `${ox}% ${oy}%` };
  }, [className]);
}

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// `closeClassName` restyles the corner close button (e.g. a red square) without touching the other dialogs.
const DialogContent = React.forwardRef(({ className, children, showClose = true, closeClassName, overlayClassName, style, ...props }, ref) => {
  const originStyle = useDialogEnterStyle(className);
  return (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-zinc-900 p-6 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      // originStyle LAST so its entrance vars (origin/scale/translate) win over any
      // a caller passed, while the caller's other style props are preserved.
      style={{ ...style, ...(originStyle || {}) }}
      {...props}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close className={cn('absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none', closeClassName)}>
          <IconX size={16} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('flex flex-col space-y-1.5 mb-4', className)} {...props} />
);

const DialogFooter = ({ className, ...props }) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6', className)} {...props} />
);

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight text-zinc-900 dark:text-zinc-100', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-zinc-500 dark:text-zinc-400', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
